import React from "react";
import { useCurrentUserStore } from "@src/state/currentUserState";
import { getHasPermission } from "@src/utils/permission";
import { Navigate } from "react-router-dom";

interface Permission {
  slug: string;
  level: string;
}

interface RequiresPermissionContainerProps {
  children?: React.ReactNode;
  element?: React.ReactNode;
  fallback?: React.ReactNode;
  permission?: Permission | null;
  permissions?: Permission[]; // Note: This will be an OR operation (user only needs one permission in the list). For AND operations, wrap the component in multiple containers
}

export default function RequiresPermissionContainer({
  permission = null,
  permissions = [],
  children,
  element,
  fallback = null,
}: RequiresPermissionContainerProps): React.ReactNode | null {
  const { currentUser } = useCurrentUserStore();
  const isRoute = !!element;

  if (!currentUser) {
    return fallback;
  }

  const requiresPermission = !!permission;
  const requiresOneOfPermissions = permissions.length > 0;

  if (
    (!requiresPermission && !requiresOneOfPermissions) ||
    (requiresPermission && requiresOneOfPermissions) ||
    (!children && !element)
  ) {
    // Should only pass in one or the other, also require children or an element
    console.warn("Invalid configuration for RequiresPermissionContainer!");
    return fallback;
  }

  let hasPermission = false;
  if (requiresPermission) {
    const { slug, level } = permission;
    hasPermission = getHasPermission(currentUser, slug, level);
  } else if (requiresOneOfPermissions) {
    let hasOneOfPermissions = false;

    permissions.map(({ slug, level }) => {
      if (!hasOneOfPermissions && getHasPermission(currentUser, slug, level)) {
        hasOneOfPermissions = true;
      }
    });

    if (hasOneOfPermissions) {
      hasPermission = true;
    }
  }

  const content = isRoute ? <>{element}</> : children;
  if (isRoute) {
    fallback = <Navigate to="/" replace />;
  }
  return hasPermission ? content : fallback;
}
